<template>
  <div>
    <template v-if="hasMultipleTagesordnungSlots">
      <ButtonSwitcher
        v-if="switcher === 'button'"
        v-model="selectedDay"
        :items="tagesordnungButtons"
        :tooltip="$t('tagesordnung_days_no_tagesordnung_tooltip_message')"
      />
      <SelectSwitcher
        v-else-if="switcher === 'select'"
        v-model="selectedDay"
        class="px-2"
        :items="tagesordnungButtons"
        item-text="fullDate"
        item-value="key"
      />
    </template>
    <slot name="top" />
    <div v-if="selectedDay" :key="selectedDay" :data-testid="`tagesordnung-item-day-${selectedDay}`" class="px-4">
      <h3
        v-if="!hasMultipleTagesordnungSlots || switcher !== 'select'"
        class="mb-2"
        data-testid="tagesordnung-item-day"
      >
        {{ fullDate(selectedDay) }}
      </h3>
      <p class="show-line-breaks">{{ meeting.tagesordnung_slot_notes[selectedDay] }}</p>
      <v-list v-if="tagesordnungItemsForSelectedDay.length" data-testid="tagesordnung-item-list" two-line subheader>
        <template v-for="(tagesordnungItem, index) in tagesordnungItemsForSelectedDay">
          <v-divider
            v-if="compact && index !== 0 && !tagesordnungItem.agenda_item.is_paragraph"
            :key="`tagesordnung-item-divider-${index}`"
          />
          <v-list-item
            :key="`${tagesordnungItem.day}-${tagesordnungItem.agenda_item.id}-${tagesordnungItem.order}`"
            :to="agendaItemLink(tagesordnungItem)"
            color="primary"
            exact
            data-testid="tagesordnung-item"
            :class="{ 'mt-4': tagesordnungItem.agenda_item.is_paragraph }"
          >
            <v-list-item-content>
              <v-list-item-title
                data-testid="tagesordnung-item-title"
                :class="{ 'font-weight-bold': tagesordnungItem.agenda_item.is_paragraph, 'text-wrap': true }"
              >
                {{ tagesordnungItem.agenda_item.title }}
              </v-list-item-title>
              <v-list-item-subtitle v-if="!compact" data-testid="tagesordnung-item-notes">
                {{ $t('tagesordnung_notes', { notes: tagesordnungItem.notes || '-' }) }}
              </v-list-item-subtitle>
              <v-list-item-subtitle v-if="!compact" data-testid="tagesordnung-item-responsible">
                {{
                  $t('tagesordnung_responsible', {
                    responsible: tagesordnungItem.agenda_item?.business?.responsible?.title || '-',
                  })
                }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <TagesordnungLiveMeetingButton
              :meeting="meeting"
              :tagesordnung-item="tagesordnungItem"
              @missing="catchMissingTagesordnungItem"
            />
          </v-list-item>
        </template>
      </v-list>
      <div v-else-if="isLoadingTagesordnungItem" class="d-flex justify-center">
        <v-progress-circular size="24" width="3" indeterminate />
      </div>
      <p v-else>
        {{ $t('tagesordnung_days_no_tagesordnung_tooltip_message') }}
      </p>
    </div>
  </div>
</template>
<script>
import { toLocaleDateString, toLocaleMonthDayString, toWeekday } from '@/filters'
import { api } from '@/api'
import { createLink } from '@/api/helpers'
import { onMessage, offMessage } from '@/mqtt'

import size from 'lodash/size'

const SWITCHER_TYPES = ['button', 'select']

export default {
  props: {
    meeting: {
      type: Object,
      required: true,
    },
    switcher: {
      type: String,
      default: 'button',
      validator(value, props) {
        return SWITCHER_TYPES.includes(value)
      },
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedDay: this.getSelectedDay(),
      tagesordnungItems: [],
      isLoadingTagesordnungItem: true,
    }
  },
  computed: {
    tagesordnungButtons() {
      return this.meeting.tagesordnung_slots.map((day) => ({
        key: day,
        label: toLocaleMonthDayString(day),
        caption: toWeekday(day, this.$i18n.locale),
        disabled: this.switcher === 'button' && this.tagesordnungByDay[day].length === 0,
        fullDate: this.fullDate(day),
      }))
    },
    hasMultipleTagesordnungSlots() {
      return size(this.meeting.tagesordnung_slots) > 1
    },
    tagesordnungByDay() {
      return Object.fromEntries(
        this.meeting.tagesordnung_slots.map((day) => [day, this.tagesordnungItems.filter((item) => item.day === day)]),
      )
    },
    tagesordnungItemsForSelectedDay() {
      return this.tagesordnungByDay[this.selectedDay] ?? []
    },
  },
  mounted() {
    this.fetchTagesordnungItems()
    onMessage('tagesordnung_item/is_active_changed', this.updateActiveTagesordnungItem)
  },
  beforeDestroy() {
    offMessage('tagesordnung_item/is_active_changed', this.updateActiveTagesordnungItem)
  },
  methods: {
    toLocaleDateString,
    toWeekday,
    fullDate(day) {
      return `${toWeekday(day)} ${toLocaleDateString(day)}`
    },
    async fetchTagesordnungItems() {
      const { data } = await api.get(createLink('meetings/{id}/tagesordnung_item', { id: this.meeting.id }))
      this.tagesordnungItems = data
      this.isLoadingTagesordnungItem = false
    },
    agendaItemLink(tagesordnungItem) {
      if (tagesordnungItem.agenda_item.is_paragraph) {
        return
      }
      return {
        name: 'agendaitem',
        params: { id: tagesordnungItem.agenda_item.id },
        query: { tagesordnungitem_id: tagesordnungItem.id },
      }
    },
    getSelectedDay() {
      const today = new Date()
      let date
      if (today < this.meeting.start || this.meeting.concluded < today) {
        date = this.meeting.start
      } else {
        date = today
      }
      this.selectedDay = date.toISOString().split('T')[0]
      return this.selectedDay
    },
    updateActiveTagesordnungItem({ active_tagesordnung_items: activeTagesordnungItems }) {
      const activeItemIds = activeTagesordnungItems.map((item) => item.id)

      for (const item of this.tagesordnungItems) {
        item.is_active = activeItemIds.includes(item.id)
      }
    },
    async catchMissingTagesordnungItem() {
      await this.fetchTagesordnungItems()
      // todo translate
      this.notifyWarning(this.$t('tagesordnung_list_notify_missing_item'))
    },
  },
}
</script>
